import { useEffect } from 'react';
import { createSelector } from '@reduxjs/toolkit';

import type { IClientContract, IRemoveSuspendAgentModalOptions } from '@/api/types';
import type { IContract } from '@/store/types';

import { useAppDispatch, useAppSelector } from '@/hooks';
import { selectAgentPositions } from '@/store/selectors/agentPositionSelectors';
import { selectClientRestermReasons, selectRestermReasonList } from '@/store/selectors/contractSelectors';
import { getClientRestermReasonsList, getRestermReasonsList } from '@/store/slices/contractSlice';
import { selectAuthMenuType } from '@/store/selectors/authSelectors';
import { UserType } from '@/store/enums';

interface IReturn {
  removeSuspendOptions: IRemoveSuspendAgentModalOptions | null;
}

const useModalOptionsSelector = createSelector(
  selectAgentPositions,
  selectClientRestermReasons,
  selectRestermReasonList,
  selectAuthMenuType,
  (agentPositions, suRestermReasons, spRestermReasons, userType) => ({
    agentPositions,
    restermReasons: userType === UserType.agent ? spRestermReasons : suRestermReasons,
    userType,
  }),
);

export interface IUserInfo {
  clientId: number;
  clientName: string;
  contractAssnStartBlock: number;
  contractId: number;
  contractName: string;
  contractStartBlock: number;
  firstName: string;
  lastName: string;
  userId: number;
}

const useModalOptions = (agent: IUserInfo | null, contract?: IContract | IClientContract): IReturn => {
  const dispatch = useAppDispatch();
  const { restermReasons, userType } = useAppSelector(useModalOptionsSelector);

  useEffect(() => {
    if (restermReasons === null) {
      const getReasons = userType === UserType.agent ? getRestermReasonsList : getClientRestermReasonsList;
      dispatch(getReasons());
    }
  }, [restermReasons, dispatch, userType]);

  if (!agent || !contract) return { removeSuspendOptions: null };

  const removeSuspendOptions = {
    userId: agent.userId,
    agentName: `${agent.firstName} ${agent.lastName}`,
    agentNameFirst: agent.firstName,
    agentNameLast: agent.lastName,
    contractId: agent.contractId,
    contractName: agent.contractName,
    contractStartBlock: agent.contractStartBlock,
    contractAssnStartBlock: agent.contractAssnStartBlock,
    positionId: contract.agentPositionId,
    positionName: contract.jobName,
    clientId: agent.clientId,
    clientName: agent.clientName,
    futureContact: true,
  };

  return {
    removeSuspendOptions: removeSuspendOptions,
  };
};

export { useModalOptions };
